import type { ComponentType } from 'react';
import {
    FEATURES,
    NavGroup,
    NavItemType,
    routingConfig,
    type FeatureName,
    type INavItemPresentation
} from '@makemydeal/dr-sales-checkin-types';
import type { NavViewContext } from '../../types/showroomNavTypes';
import { AddIcon } from '../../components/Icons/AddIcon/AddIcon';
import { DealSummaryIcon } from '../../components/Icons/DealSummaryIcon/DealSummaryIcon';
import { FinancingIcon } from '../../components/Icons/FinancingIcon/FinancingIcon';
import { TradeInIcon } from '../../components/Icons/TradeInIcon/TradeInIcon';
import { VehicleDetailIcon } from '../../components/Icons/VehicleDetailIcon/VehicleDetailIcon';
import { TrashIcon } from '../../components/Icons/TrashIcon/TrashIcon';
import { SendToDeskIcon } from '../../components/Icons/SendToDeskIcon/SendToDeskIcon';

/**
 * Static presentational metadata per nav entity `key` (US2055174).
 *
 * This is the hook-layer registry — the "static look" of each item — kept out of
 * Redux. Adding/relabelling/reordering an item is a change here plus its Redux
 * entity + command builder; components never change.
 */
export const navItemRegistry: Partial<Record<FeatureName, INavItemPresentation>> = {
    // Top nav
    [FEATURES.BUYER]: { type: NavItemType.CustomerName, title: '', order: 0, placement: 'left' },
    [FEATURES.BUYER_INBOX]: { type: NavItemType.Inbox, title: 'Inbox', order: 1, placement: 'left' },
    [FEATURES.ADD_COBUYER]: {
        type: NavItemType.AddCobuyer,
        title: 'Add Co-Buyer',
        order: 2,
        placement: 'left',
        iconKey: 'addCobuyer'
    },
    [FEATURES.COBUYER]: { type: NavItemType.Cobuyer, title: '', order: 3, placement: 'left', iconKey: 'removeCobuyer' },
    [FEATURES.COBUYER_INBOX]: { type: NavItemType.Inbox, title: 'Inbox', order: 4, placement: 'left' },
    [FEATURES.SEND_TO_DESK]: {
        type: NavItemType.SendToDesk,
        title: 'Send to Desk',
        order: 5,
        placement: 'right',
        iconKey: 'sendToDesk'
    },
    [FEATURES.DEAL_COMMENTS]: { type: NavItemType.DealComments, title: 'Deal Comments', order: 6, placement: 'right' },
    [FEATURES.SHARE_DROPDOWN]: { type: NavItemType.ShareDropdown, title: 'Share', order: 7, placement: 'right' },
    // Share dropdown contents
    [FEATURES.CREDIT_APP]: { type: NavItemType.Link, title: 'Credit App', order: 0 },
    [FEATURES.DOC_UPLOAD]: { type: NavItemType.Link, title: 'Doc Upload', order: 1 },
    [FEATURES.DEAL_PDF]: { type: NavItemType.Link, title: 'Deal PDF', order: 2 },
    // Left nav — "Build Your Offer" cards
    [FEATURES.VEHICLE_DETAILS]: {
        type: NavItemType.Card,
        title: 'Vehicle Details',
        description: 'Learn about this vehicle',
        iconKey: 'vehicleDetails',
        path: routingConfig.VEHICLE_DETAILS,
        order: 0
    },
    [FEATURES.TRADE_IN]: {
        type: NavItemType.Card,
        title: 'Trade-In',
        description: 'Value your vehicle',
        iconKey: 'tradeIn',
        path: routingConfig.TRADE_IN,
        order: 1
    },
    [FEATURES.CUSTOMER_DETAILS]: {
        type: NavItemType.Card,
        title: 'Customer Details',
        description: 'Add Customer Info',
        iconKey: 'financing',
        path: routingConfig.FINANCING,
        order: 2
    },
    [FEATURES.DEAL_SUMMARY]: {
        type: NavItemType.Card,
        title: 'Deal Summary',
        description: 'Review, submit & status',
        iconKey: 'dealSummary',
        path: routingConfig.DEAL_SUMMARY,
        order: 3
    },
    // Quick links
    [FEATURES.BROWSE_INVENTORY]: { type: NavItemType.Link, title: 'Browse Inventory', path: routingConfig.INVENTORY, order: 0 },
    [FEATURES.MANAGE_DOCUMENTS]: {
        type: NavItemType.Link,
        title: 'Manage Documents',
        path: routingConfig.MANAGE_DOCUMENTS,
        tooltip: 'Add a vehicle to this deal to manage documents.',
        order: 1
    },
    [FEATURES.RECENT_DEALS]: { type: NavItemType.Link, title: 'Recent Deals', path: routingConfig.RECENT_DEALS, order: 2 },
    [FEATURES.INSURANCE]: { type: NavItemType.Link, title: 'Insurance', path: routingConfig.INSURANCE, order: 3 }
};

/**
 * Resolves a serializable `iconKey` to a concrete icon component. Icons can't live
 * in Redux, so entities reference them by key and the hook resolves them here.
 */
export const iconByKey: Record<string, ComponentType> = {
    vehicleDetails: VehicleDetailIcon,
    tradeIn: TradeInIcon,
    financing: FinancingIcon,
    dealSummary: DealSummaryIcon,
    addCobuyer: AddIcon,
    removeCobuyer: TrashIcon,
    sendToDesk: SendToDeskIcon
};

/**
 * Runtime badge counts per entity `key`. Badges are live data rather than static
 * presentation, so they're declared here as context readers — extend this map, not the
 * assembly pipeline, when another item needs one. Keys with no entry get no badge.
 */
export const badgeByKey: Partial<Record<FeatureName, (ctx: NavViewContext) => number | undefined>> = {
    [FEATURES.RECENT_DEALS]: (ctx) => ctx.recentDealsCount
};

/**
 * Runtime labels per entity `key`, for the items whose text is live data rather than a
 * static title — the customer names, read from the `customer` slice via context. Keys
 * with no entry fall back to their registry `title`.
 */
export const labelByKey: Partial<Record<FeatureName, (ctx: NavViewContext) => string>> = {
    [FEATURES.BUYER]: (ctx) => ctx.buyerLabel,
    [FEATURES.COBUYER]: (ctx) => ctx.cobuyerLabel
};

export const sectionMetaByGroup: Partial<Record<NavGroup, { key: string; title: string }>> = {
    [NavGroup.LeftNav]: { key: 'buildYourOffer', title: 'Build Your Offer' },
    [NavGroup.QuickLink]: { key: 'quickLinks', title: 'Quick Links' }
};

export const BUILD_YOUR_OFFER_GROUP = sectionMetaByGroup[NavGroup.LeftNav]!.key;
